import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { withComplianceOverride } from '../../utils/complianceOverride';
import ReasonBadge from '../../components/ReasonBadge';
import {
  Plus, Clock, UserCog, Tag, CalendarDays, RotateCcw, Unlock, Check, XCircle,
  Trash2, Download, X, RefreshCw, Search, Calculator,
} from 'lucide-react';

const pad = n => String(n).padStart(2, '0');
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const weekday = d => d ? new Date(d).toLocaleDateString('en-AU', { weekday: 'short' }) : '—';
const fmtTime = t => {
  if (!t) return '—';
  const [h, m] = String(t).split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM'; const hh = h % 12 === 0 ? 12 : h % 12;
  return `${pad(hh)}:${pad(m || 0)} ${ap}`;
};
const hoursOf = s => {
  if (s.total_hours != null) return Number(s.total_hours).toFixed(2);
  if (!s.start_time || !s.end_time) return '—';
  const [sh, sm] = s.start_time.split(':').map(Number);
  const [eh, em] = s.end_time.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm); if (mins < 0) mins += 1440;
  return (mins / 60).toFixed(2);
};

const STATUS_PILL = {
  completed: 'bg-emerald-50 text-emerald-700', booked: 'bg-blue-50 text-blue-700',
  pending: 'bg-amber-50 text-amber-700', available: 'bg-violet-50 text-violet-700',
  awaiting_confirmation: 'bg-amber-50 text-amber-700', invoiced: 'bg-teal-50 text-teal-700',
  cancelled: 'bg-red-50 text-red-700', cancelled_with_charge: 'bg-red-50 text-red-700', declined: 'bg-slate-100 text-slate-500',
};
const statusLabel = s => (s || '').replace(/_/g, ' ');

// ── Bulk edit modal (time / worker / service / day) ───────────────────────────
function BulkEditModal({ op, count, staff, serviceTypes, busy, onClose, onRun }) {
  const TITLES = { time: 'Change Time', worker: 'Switch Worker', service: 'Change Service Type', day: 'Change Day' };
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [nextDay, setNextDay] = useState(false);
  const [staffId, setStaffId] = useState('');
  const [force, setForce] = useState(false);
  const [serviceType, setServiceType] = useState('');
  const [days, setDays] = useState(7);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">{TITLES[op]} <span className="text-slate-400 font-normal">· {count} selected</span></h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {op === 'time' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Start</label><input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">End</label><input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={nextDay} onChange={e => setNextDay(e.target.checked)} /> Ends next day (overnight)</label>
            </>
          )}
          {op === 'worker' && (
            <>
              <label className="block text-xs font-medium text-slate-600 mb-1">Assign to</label>
              <select value={staffId} onChange={e => setStaffId(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Select worker…</option>
                {(staff || []).map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} /> Assign even if it causes a clash</label>
              <button onClick={() => onRun('switch-worker', { staffId: null }, 'Unassigned')} disabled={busy} className="text-sm text-slate-500 hover:text-slate-700 underline">Remove worker (leave unassigned)</button>
            </>
          )}
          {op === 'service' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Service type</label>
              <select value={serviceType} onChange={e => setServiceType(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Select…</option>
                {(serviceTypes || []).filter(t => t.is_active !== false).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
          )}
          {op === 'day' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Move by (days)</label>
              <input type="number" value={days} onChange={e => setDays(e.target.value)} className="w-40 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <p className="text-xs text-slate-400 mt-1">Negative moves earlier. Clashes are flagged, not blocked.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button disabled={busy} className="px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60" onClick={() => {
            if (op === 'time') onRun('change-time', { startTime, endTime, nextDay }, 'Retimed');
            else if (op === 'worker') { if (!staffId) return toast.error('Pick a worker'); onRun('switch-worker', { staffId, force }, 'Reassigned'); }
            else if (op === 'service') { if (!serviceType) return toast.error('Pick a service type'); onRun('change-service-type', { serviceType }, 'Service changed'); }
            else if (op === 'day') onRun('change-day', { days: parseInt(days, 10) }, 'Moved');
          }}>{busy ? 'Working…' : 'Apply'}</button>
        </div>
      </div>
    </div>
  );
}

// Bulk Edit list. Opened from the scheduler it starts on the dates the calendar is showing
// (defaultFrom/defaultTo) and the branch the board is filtered to, and follows the calendar when
// the week changes, so it never opens on every shift since the start of time.
export default function ServiceScheduleList({
  embedded = false, staff: staffProp, serviceTypes: stProp, clients: clientsProp,
  defaultFrom = '', defaultTo = '', locationId = '',
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState(staffProp || []);
  const [serviceTypes, setServiceTypes] = useState(stProp || []);
  const [clients, setClients] = useState(clientsProp || []);
  const [clientFilter, setClientFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  // The calendar moved to another week/month: show that period.
  useEffect(() => { setFrom(defaultFrom); setTo(defaultTo); }, [defaultFrom, defaultTo]);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [op, setOp] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.start_date = from;
      if (to) params.end_date = to;
      if (locationId) params.location_id = locationId;
      const { data } = await api.get('/shifts', { params });
      setRows(Array.isArray(data) ? data : (data?.shifts || []));
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* on date or branch change */ }, [from, to, locationId]);
  useEffect(() => {
    if (staffProp?.length) { setStaff(staffProp); return; }
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : (r.data?.staff || []))).catch(() => {});
  }, [staffProp]);
  useEffect(() => {
    if (stProp?.length) { setServiceTypes(stProp); return; }
    api.get('/service-types').then(r => setServiceTypes(Array.isArray(r.data) ? r.data : (r.data?.serviceTypes || []))).catch(() => {});
  }, [stProp]);
  useEffect(() => {
    if (clientsProp?.length) { setClients(clientsProp); return; }
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || []))).catch(() => {});
  }, [clientsProp]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cfObj = clientFilter ? clients.find(c => c.id === clientFilter) : null;
    const cfName = cfObj ? `${cfObj.first_name} ${cfObj.last_name}`.toLowerCase() : '';
    return rows.filter(s => {
      // Per-client filter: match the shift's client_ids (preferred) or the client name string.
      if (clientFilter) {
        const idList = (s.client_ids || '').split(',').map(x => x.trim()).filter(Boolean);
        const byId = idList.includes(clientFilter);
        const byName = cfName && (s.client_names || '').toLowerCase().includes(cfName);
        if (!byId && !byName) return false;
      }
      if (!q) return true;
      return (s.client_names || '').toLowerCase().includes(q) ||
        (s.staff_name || '').toLowerCase().includes(q) ||
        (s.shift_type || '').toLowerCase().includes(q) ||
        (s.status || '').toLowerCase().includes(q);
    });
  }, [rows, search, clientFilter, clients]);

  useEffect(() => { setPage(1); }, [search, from, to, pageSize, clientFilter]);

  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);
  const allFilteredSelected = total > 0 && filtered.every(s => selected.has(s.id));

  const toggle = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(prev => {
    if (filtered.every(s => prev.has(s.id))) { const n = new Set(prev); filtered.forEach(s => n.delete(s.id)); return n; }
    const n = new Set(prev); filtered.forEach(s => n.add(s.id)); return n;
  });

  const ids = () => [...selected];
  const need = () => { if (!selected.size) { toast.error('Select at least one shift'); return false; } return true; };

  const runBulk = async (path, body, label, keepSel) => {
    setBusy(true);
    try {
      const { data } = await withComplianceOverride(confirm, (extra) => api.post(`/shifts/bulk/${path}`, { ids: ids(), ...extra, ...body }));
      const done = data.updated ?? data.reverted ?? data.recalculated ?? data.deleted ?? 0;
      toast.success(`${label}: ${done} shift${done !== 1 ? 's' : ''}`);
      if (data.skipped?.length) toast(`${data.skipped.length} skipped`, { icon: '⚠️' });
      if (data.conflicts?.length) toast(`${data.conflicts.length} clash${data.conflicts.length !== 1 ? 'es' : ''} flagged`, { icon: '⚠️' });
      setOp(null);
      if (!keepSel) setSelected(new Set());
      load();
    } catch (e) { if (!e.cancelled) toast.error(e.response?.data?.message || 'Bulk action failed'); }
    finally { setBusy(false); }
  };
  const bulkAction = async (action, label) => {
    setBusy(true);
    try {
      const { data } = await api.post('/shifts/bulk-action', { ids: ids(), action });
      const n = data?.updated ?? selected.size;
      toast.success(`${label}: ${n} shift${n !== 1 ? 's' : ''}`);
      if (data?.skipped) toast(`${data.skipped} skipped: already completed, invoiced or cancelled`, { icon: '⚠️' });
      setSelected(new Set()); load();
    }
    catch { toast.error('Bulk action failed'); } finally { setBusy(false); }
  };

  const onOp = (o) => { if (need()) setOp(o); };
  const onUnlock = async () => { if (!need()) return; if (await confirm(`Unlock ${selected.size} shift(s)? Completed/approved shifts revert to editable and their timesheets are removed. Invoiced shifts are skipped.`, { title: 'Unlock shifts', danger: true })) runBulk('undo-complete', {}, 'Unlocked'); };
  const onComplete = () => { if (need()) bulkAction('complete', 'Completed'); };
  const onCancel = async () => { if (!need()) return; if (await confirm(`Cancel ${selected.size} shift(s)?`, { title: 'Cancel shifts', danger: true })) bulkAction('cancel', 'Cancelled'); };
  const onDelete = async () => { if (!need()) return; if (await confirm(`Permanently delete ${selected.size} shift(s)? This cannot be undone. Invoiced shifts are skipped.`, { title: 'Delete shifts', danger: true })) runBulk('delete', {}, 'Deleted'); };
  const onRecalc = () => { if (need()) runBulk('recalc-fees', {}, 'Recalculated', true); };

  const exportCsv = () => {
    const src = selected.size ? filtered.filter(s => selected.has(s.id)) : filtered;
    if (!src.length) return toast.error('Nothing to export');
    const head = ['Name', 'Type', 'Employee', 'Status', 'Weekday', 'Date', 'From', 'To', 'Hours'];
    const csv = [head.join(',')].concat(src.map(s => [
      `"${(s.client_names || '').replace(/"/g, '""')} - ${(s.shift_type || '').replace(/"/g, '""')}"`,
      s.shift_kind || 'Individual', `"${(s.staff_name || 'Unassigned').replace(/"/g, '""')}"`,
      statusLabel(s.status), weekday(s.shift_date), s.shift_date || '', s.start_time || '', s.end_time || '', hoursOf(s),
    ].join(','))).join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'service-schedule.csv'; a.click(); window.URL.revokeObjectURL(url);
  };

  const Btn = ({ icon: Icon, label, onClick, danger, primary }) => (
    <button onClick={onClick} disabled={busy}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 ${
        danger ? 'border-red-200 text-red-600 hover:bg-red-50' : primary ? 'border-primary-200 text-primary-700 hover:bg-primary-50' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div className={embedded ? 'flex-1 overflow-y-auto' : 'p-4 max-w-[1400px] mx-auto'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-slate-800">Service Schedule</h1>
          <button onClick={() => navigate('/scheduler')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-slate-200 hover:bg-slate-50 text-slate-600"><X size={15} /> Close</button>
        </div>
      )}

      {/* Sticky controls — stay visible while scrolling the table */}
      <div className={embedded ? 'sticky top-0 z-20 bg-white/95 backdrop-blur px-4 pt-3 pb-1' : ''}>
      {/* Action toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-2 mb-3">
        <Btn icon={Plus} label="New" onClick={() => navigate('/scheduler')} primary />
        <div className="w-px h-5 bg-slate-200 mx-0.5" />
        <Btn icon={Clock} label="Change Time" onClick={() => onOp('time')} />
        <Btn icon={UserCog} label="Switch Worker" onClick={() => onOp('worker')} />
        <Btn icon={Tag} label="Change Service Type" onClick={() => onOp('service')} />
        <Btn icon={CalendarDays} label="Change Day" onClick={() => onOp('day')} />
        <Btn icon={Calculator} label="Recalc" onClick={onRecalc} />
        <Btn icon={Unlock} label="Unlock" onClick={onUnlock} />
        <div className="w-px h-5 bg-slate-200 mx-0.5" />
        <Btn icon={Check} label="Complete" onClick={onComplete} />
        <Btn icon={XCircle} label="Cancel Shift" onClick={onCancel} />
        <Btn icon={Trash2} label="Delete" onClick={onDelete} danger />
        <Btn icon={Download} label="Export" onClick={exportCsv} />
        <button onClick={load} disabled={busy} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 ml-auto disabled:opacity-50"><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-1.5">
          <CalendarDays size={14} className="text-slate-400" />
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          <span className="text-slate-400 text-sm">to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          {(defaultFrom || defaultTo) && (from !== defaultFrom || to !== defaultTo) && (
            <button onClick={() => { setFrom(defaultFrom); setTo(defaultTo); }}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50">
              Calendar dates
            </button>
          )}
        </div>
        {/* Per-client filter — pick a client, see only their shifts, then bulk-edit them */}
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} title="Filter to one client"
          className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm max-w-[200px] focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">All clients</option>
          {[...clients].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)).map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
        </select>
        {clientFilter && (
          <button onClick={() => setSelected(new Set(filtered.map(s => s.id)))}
            className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-primary-200 text-primary-700 hover:bg-primary-50">
            Select all {filtered.length}
          </button>
        )}
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client, worker, service, status…" className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ml-auto">
          {[25, 50, 100, 200].map(n => <option key={n} value={n}>Show {n}</option>)}
        </select>
      </div>
      </div>{/* end sticky controls */}

      {/* Table */}
      <div className={embedded ? 'px-4 pb-4' : ''}>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <th className="px-3 py-2.5 w-8"><input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="rounded border-slate-300 accent-primary-600" /></th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Name</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Type</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Employee</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Status</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Weekday</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Start Day</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Time From</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500">Time To</th>
                <th className="text-right px-3 py-2.5 text-xs font-medium text-slate-500">Total Hours</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-10 text-slate-400 text-sm">Loading…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-slate-400 text-sm">No shifts found{(from || to) ? ' for this date range' : ''}.</td></tr>
              ) : pageRows.map(s => (
                <tr key={s.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${selected.has(s.id) ? 'bg-primary-50/40' : ''}`}>
                  <td className="px-3 py-2.5"><input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} className="rounded border-slate-300 accent-primary-600" /></td>
                  <td className="px-3 py-2.5 text-slate-700">{s.client_names || '—'}{s.shift_type ? <span className="text-slate-400"> · {s.shift_type}</span> : ''}</td>
                  <td className="px-3 py-2.5 text-slate-600">{s.shift_kind || 'Individual'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{s.staff_name || <span className="text-amber-600">Unassigned</span>}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${STATUS_PILL[s.status] || 'bg-slate-100 text-slate-600'}`}>{statusLabel(s.status)}</span>
                      <ReasonBadge shift={s} />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{weekday(s.shift_date)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{fmtDate(s.shift_date)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{fmtTime(s.start_time)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{fmtTime(s.end_time)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{hoursOf(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer / pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span>
            {total === 0 ? 'No entries' : `Showing ${(page - 1) * pageSize + 1} to ${Math.min(page * pageSize, total)} of ${total} entries`}
            {selected.size > 0 && <span className="ml-2 font-medium text-primary-700">· {selected.size} selected</span>}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-2.5 py-1 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">←</button>
            <span className="px-2 text-slate-600">Page {page} / {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className="px-2.5 py-1 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">→</button>
          </div>
        </div>
      </div>
      </div>

      {op && (
        <BulkEditModal op={op} count={selected.size} staff={staff} serviceTypes={serviceTypes} busy={busy}
          onClose={() => setOp(null)} onRun={runBulk} />
      )}
    </div>
  );
}
